import React, { useCallback, useEffect, useId, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import { confirmDialog } from '../ConfirmDialog';
import { toast } from '../Toast';
import { deleteBrand, upsertBrand, uploadBrandLogo } from '../../services/brandsService';
import { buildBrandPayload } from '../../Utils/brandForm';
import { useMyBrands } from '../../hooks/useMyBrands';
import BrandEditor from './BrandEditor';
import BrandList from './BrandList';
import {
  brandsAddButtonSx,
  brandsCloseButtonSx,
  brandsDialogActionsSx,
  brandsDialogContentSx,
  brandsDialogHeaderDividerSx,
  brandsDialogPaperSx,
  brandsDialogSubtitleSx,
  brandsDialogTitleBarSx,
  brandsDialogTitleSx,
  brandsErrorTextSx,
  brandsIconButtonSx,
  brandsListWellSx,
  brandsSecondaryButtonSx,
  brandsSkeletonSx,
  brandsSubtitleRowSx,
  brandsTooltipSlotProps,
  BRANDS_MODAL_Z_INDEX,
} from './BrandsModal.styles';
import {
  appModalPrimaryButtonSx,
  appModalSecondaryButtonSx,
} from '../appModalChrome.styles';

function BrandsModalSkeleton() {
  return (
    <Box sx={brandsListWellSx} aria-hidden="true">
      {[0, 1].map((key) => (
        <Skeleton key={key} variant="rounded" height={64} sx={brandsSkeletonSx} />
      ))}
    </Box>
  );
}

/**
 * Identidades del usuario (0..N). Reemplaza el perfil 1:1 (`/getuserbranding`)
 * y el ABM colgado de empresa: el dueño es el JWT.
 *
 * @param {{ open: boolean, startOnCreate?: boolean, onClose: () => void }} props
 * @see docs/maps/brands.md
 */
export default function BrandsModal({ open, startOnCreate = false, onClose }) {
  const {
    loading,
    error,
    brands,
    canCreateBrand,
    reload,
  } = useMyBrands({ enabled: open });

  const formId = `brand-editor-form${useId()}`;
  const [view, setView] = useState('list');
  const [editingBrand, setEditingBrand] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [busyBrandId, setBusyBrandId] = useState('');
  const [actionError, setActionError] = useState('');

  useEffect(() => {
    if (!open) {
      setView('list');
      setEditingBrand(null);
      setSaving(false);
      setUploadingLogo(false);
      setBusyBrandId('');
      setActionError('');
      return;
    }
    // "Usar mi identidad" abre directo el alta: no tiene sentido pasar por la lista
    // vacía, y evita un Dialog extra encima del de compartir.
    if (startOnCreate) {
      setEditingBrand(null);
      setActionError('');
      setView('editor');
    }
  }, [open, startOnCreate]);

  const backToList = useCallback(() => {
    setView('list');
    setEditingBrand(null);
    setActionError('');
  }, []);

  const handleSaveBrand = async (form, { makeDefault, logoFile }) => {
    const brandId = editingBrand?.id || '';
    setSaving(true);
    setActionError('');

    const result = await upsertBrand(buildBrandPayload(form, { brandId, makeDefault }));

    if (!result.ok) {
      setSaving(false);
      setActionError(result.message || 'No se pudo guardar la identidad.');
      return;
    }

    // El logo que se eligió antes de que la marca existiera sube ahora: la key
    // de S3 necesita el id que recién devolvió el alta.
    if (logoFile && result.brand?.id) {
      setUploadingLogo(true);
      const uploaded = await uploadBrandLogo({ brandId: result.brand.id, file: logoFile });
      setUploadingLogo(false);

      if (!uploaded.ok) {
        setSaving(false);
        reload();
        // La marca quedó creada: se sigue en el editor, ya en modo edición,
        // para reintentar solo el logo.
        setEditingBrand(result.brand);
        setActionError(uploaded.message || 'La identidad se guardó, pero no se pudo subir el logo.');
        return;
      }
    }

    setSaving(false);
    reload();
    toast.success(brandId ? 'Identidad guardada.' : 'Identidad creada.');
    backToList();
  };

  const handleUploadLogo = async (file) => {
    if (!editingBrand?.id) return;
    setUploadingLogo(true);
    setActionError('');
    const result = await uploadBrandLogo({ brandId: editingBrand.id, file });
    setUploadingLogo(false);
    if (!result.ok) {
      setActionError(result.message || 'No se pudo subir el logo.');
      return;
    }
    setEditingBrand(result.brand);
    reload();
    toast.success('Logo actualizado.');
  };

  const handleRemoveLogo = async () => {
    if (!editingBrand?.id) return;
    setUploadingLogo(true);
    setActionError('');
    const result = await upsertBrand({ brand_id: editingBrand.id, logo_url: '' });
    setUploadingLogo(false);
    if (!result.ok) {
      setActionError(result.message || 'No se pudo quitar el logo.');
      return;
    }
    setEditingBrand(result.brand);
    reload();
    toast.success('Logo eliminado.');
  };

  const handleSetDefault = async (brand) => {
    setBusyBrandId(brand.id);
    const result = await upsertBrand({ brand_id: brand.id, is_default: true });
    setBusyBrandId('');
    if (!result.ok) {
      toast.error(result.message || 'No se pudo cambiar la identidad principal.');
      return;
    }
    reload();
    toast.success(`${brand.brand_name} es tu identidad principal.`);
  };

  const handleDeleteBrand = async (brand) => {
    const { isConfirmed } = await confirmDialog({
      title: `¿Eliminar ${brand.brand_name}?`,
      description: 'Los enlaces que la usaban pasan a tu identidad principal.',
      confirmLabel: 'Eliminar',
      variant: 'danger',
    });
    if (!isConfirmed) return;

    setBusyBrandId(brand.id);
    const result = await deleteBrand(brand.id);
    setBusyBrandId('');
    if (!result.ok) {
      toast.error(result.message || 'No se pudo eliminar la identidad.');
      return;
    }
    reload();
    toast.success('Identidad eliminada.');
  };

  const isEditorView = view === 'editor';
  const canAddBrand = canCreateBrand;
  const openCreate = () => {
    setEditingBrand(null);
    setActionError('');
    setView('editor');
  };
  const showAddIdentity = !isEditorView && !loading && !error && canAddBrand;

  const renderBody = () => {
    if (loading) return <BrandsModalSkeleton />;

    if (error) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Typography sx={brandsErrorTextSx}>{error}</Typography>
          <Button variant="outlined" onClick={reload} sx={{ ...brandsSecondaryButtonSx, alignSelf: 'flex-start' }}>
            Reintentar
          </Button>
        </Box>
      );
    }

    if (isEditorView) {
      return (
        <BrandEditor
          formId={formId}
          brand={editingBrand}
          saving={saving}
          uploadingLogo={uploadingLogo}
          error={actionError}
          onSave={handleSaveBrand}
          onUploadLogo={handleUploadLogo}
          onRemoveLogo={handleRemoveLogo}
        />
      );
    }

    return (
      <BrandList
        brands={brands}
        canCreateBrand={canAddBrand}
        busyBrandId={busyBrandId}
        onCreate={openCreate}
        onEdit={(brand) => {
          setEditingBrand(brand);
          setActionError('');
          setView('editor');
        }}
        onDelete={handleDeleteBrand}
        onSetDefault={handleSetDefault}
      />
    );
  };

  const title = isEditorView
    ? (editingBrand ? 'EDITAR IDENTIDAD' : 'NUEVA IDENTIDAD')
    : 'IDENTIDAD';

  const subtitle = isEditorView
    ? 'El nombre es obligatorio. Logo, colores y contacto son opcionales.'
    : 'Las identidades con las que compartís: quien abre un enlace ve tu logo, tus colores y tus datos de contacto en lugar de la vista por defecto de Terrio.';

  return (
    <Dialog
      open={open}
      onClose={() => { if (!saving && !uploadingLogo) onClose(); }}
      maxWidth={false}
      sx={{ zIndex: BRANDS_MODAL_Z_INDEX }}
      PaperProps={{ sx: brandsDialogPaperSx }}
    >
      <DialogTitle sx={brandsDialogTitleBarSx}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, minWidth: 0, flex: 1 }}>
          {isEditorView ? (
            <Tooltip title="Volver a Identidad" arrow slotProps={brandsTooltipSlotProps}>
              <IconButton size="small" onClick={backToList} aria-label="Volver a Identidad" sx={brandsIconButtonSx}>
                <ArrowBackIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          ) : null}
          <Typography component="h2" sx={brandsDialogTitleSx}>{title}</Typography>
        </Box>
        <IconButton
          onClick={onClose}
          disabled={saving || uploadingLogo}
          aria-label="Cerrar Identidad"
          sx={brandsCloseButtonSx}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>
      <Divider sx={brandsDialogHeaderDividerSx} />
      <Box sx={brandsSubtitleRowSx}>
        <Typography sx={brandsDialogSubtitleSx}>{subtitle}</Typography>
        {showAddIdentity ? (
          <Tooltip title="Agregar nueva identidad" placement="top" arrow slotProps={brandsTooltipSlotProps}>
            <IconButton
              onClick={openCreate}
              aria-label="Agregar nueva identidad"
              sx={brandsAddButtonSx}
            >
              <AddIcon />
            </IconButton>
          </Tooltip>
        ) : null}
      </Box>
      <DialogContent sx={brandsDialogContentSx}>
        {renderBody()}
      </DialogContent>

      <DialogActions sx={brandsDialogActionsSx}>
        {isEditorView ? (
          <>
            <Button variant="outlined" onClick={backToList} disabled={saving || uploadingLogo} sx={appModalSecondaryButtonSx}>
              Cancelar
            </Button>
            <Button
              type="submit"
              form={formId}
              variant="contained"
              disabled={saving || uploadingLogo}
              startIcon={saving ? <CircularProgress size={15} thickness={5} sx={{ color: 'inherit' }} /> : null}
              sx={appModalPrimaryButtonSx}
            >
              {editingBrand ? 'Guardar' : 'Crear identidad'}
            </Button>
          </>
        ) : (
          <Button variant="outlined" onClick={onClose} sx={appModalSecondaryButtonSx}>
            Cerrar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
